// src/components/projects/dialog/ModelConfigDialog.js - Updated Header

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  alpha,
  useTheme,
  Fade,
  Slide,
  Paper,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Close,
  Check,
  CheckCircleOutline,
  WarningOutlined,
  Visibility,
  VisibilityOff,
  Key,
  CloudDone,
  ArrowForward,
  ArrowBack,
  Save,
} from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ModelConfigDialog = ({
  open,
  onClose,
  provider,
  apiKey,
  setApiKey,
  status,
  setStatus,
  errorMsg,
  setErrorMsg,
  touched,
  setTouched,
  showApiKey,
  setShowApiKey,
  handleTestCredentials,
  handleSaveCredentials,
  reconfiguringModel,
  isLoading,
  onSaveSuccess,
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const [localStatus, setLocalStatus] = useState('idle');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setTestSuccess(false);
      setLocalStatus('idle');
      setErrorMsg('');
      setApiKey('');
      setTouched(false);
    }
  }, [open]);

  const getProviderColor = () => {
    const colors = {
      anthropic: theme.palette.error.main,
      openai: theme.palette.success.main,
      gemini: theme.palette.info.main,
      deepseek: theme.palette.warning.main,
      grok: theme.palette.secondary.main,
    };
    return colors[provider?.toLowerCase()] || theme.palette.primary.main;
  };

  const getProviderIcon = () => {
    const icons = {
      anthropic: '',
      openai: '',
      gemini: '',
      deepseek: '',
      grok: '',
    };
    return icons[provider?.toLowerCase()] || '🔑';
  };

  const handleTest = async () => {
    if (!provider || !apiKey) {
      setLocalStatus('error');
      setErrorMsg('Provider and API key are required');
      return;
    }

    setIsTesting(true);
    setLocalStatus('testing');
    setTestSuccess(false);
    
    try {
      const result = await handleTestCredentials(provider, apiKey);
      
      let isSuccessful = false;
      
      if (result?.status === 200 || result?.status === 201 || result?.status === 204) {
        isSuccessful = true;
      } else if (result?.data?.message) {
        const msg = result.data.message.toLowerCase();
        if (msg.includes('success') || msg.includes('valid')) {
          isSuccessful = true;
        }
      } else if (result?.data?.success === true) {
        isSuccessful = true;
      }
      
      if (isSuccessful) {
        setTestSuccess(true);
        setActiveStep(2);
        setLocalStatus('idle');
        setErrorMsg('');
      } else {
        setTestSuccess(false);
        setLocalStatus('error');
        setErrorMsg(result?.data?.message || 'Connection failed. Please check your API key.');
      }
    } catch (error) {
      console.error('Test error:', error);
      setTestSuccess(false);
      setLocalStatus('error');
      setErrorMsg(error?.message || 'Connection failed. Please check your API key.');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (!provider || !apiKey) {
      setLocalStatus('error');
      setErrorMsg('Provider and API key are required');
      return;
    }

    if (!testSuccess) {
      setLocalStatus('error');
      setErrorMsg('Please test the connection before saving');
      return;
    }

    setIsSaving(true);
    setLocalStatus('saving');
    
    try {
      const result = await handleSaveCredentials(provider, apiKey);
      
      let isSuccessful = false;
      
      if (result?.status === 200 || result?.status === 201 || result?.status === 204) {
        isSuccessful = true;
      } else if (result?.data?.message?.toLowerCase().includes('success')) {
        isSuccessful = true;
      } else if (result?.data?.success === true) {
        isSuccessful = true;
      }
      
      if (isSuccessful) {
        setLocalStatus('success');
        
        if (onSaveSuccess) {
          await onSaveSuccess();
        }
        
        setTimeout(() => {
          onClose();
          setIsSaving(false);
          setActiveStep(0);
          setTestSuccess(false);
          setLocalStatus('idle');
        }, 1500);
      } else {
        const errorMessage = result?.data?.message || 'Failed to save credentials';
        setLocalStatus('error');
        setErrorMsg(errorMessage);
        setIsSaving(false);
      }
    } catch (error) {
      console.error('Save error:', error);
      setLocalStatus('error');
      setErrorMsg(error?.message || 'Failed to save credentials');
      setIsSaving(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!apiKey || apiKey.length < 10) {
        setTouched(true);
        return;
      }
      setActiveStep(1);
      setTimeout(() => handleTest(), 500);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    if (activeStep === 2) {
      setTestSuccess(false);
      setLocalStatus('idle');
    }
  };

  const isStepValid = () => {
    if (activeStep === 0) return apiKey && apiKey.length >= 10;
    if (activeStep === 1) return true;
    if (activeStep === 2) return testSuccess;
    return true;
  };

  const getStepStatus = (index) => {
    if (index < activeStep) return 'complete';
    if (index === activeStep) return 'active';
    return 'inactive';
  };

  const providerColor = getProviderColor();

  // Determine if this is a reconfigure or new configuration
  const isReconfiguring = reconfiguringModel && reconfiguringModel.id;
  const dialogTitle = isReconfiguring ? 'Update API Key' : 'Configure Model';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[8],
          maxWidth: 520,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          background: `linear-gradient(135deg, ${alpha(providerColor, 0.08)} 0%, ${alpha(providerColor, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                backgroundColor: alpha(providerColor, 0.1),
                color: providerColor,
              }}
            >
              {getProviderIcon()}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                {dialogTitle}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'Provider'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Step Indicator */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          {[
            { label: 'Enter Key', icon: <Key /> },
            { label: 'Test', icon: <CloudDone /> },
            { label: 'Save', icon: <Save /> },
          ].map((step, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: index < 2 ? 1 : 'auto',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: getStepStatus(index) === 'complete'
                      ? theme.palette.success.main
                      : getStepStatus(index) === 'active'
                      ? providerColor
                      : alpha(theme.palette.divider, 0.5),
                    color: getStepStatus(index) === 'inactive'
                      ? theme.palette.text.disabled
                      : '#fff',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {getStepStatus(index) === 'complete' ? (
                    <Check sx={{ fontSize: 16 }} />
                  ) : (
                    <Typography variant="caption" fontWeight={600}>
                      {index + 1}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.55rem',
                    fontWeight: getStepStatus(index) === 'active' ? 600 : 400,
                    color: getStepStatus(index) === 'inactive'
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary,
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
              {index < 2 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    mx: 1,
                    backgroundColor: getStepStatus(index) === 'complete'
                      ? theme.palette.success.main
                      : alpha(theme.palette.divider, 0.3),
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        {/* Step Content */}
        <Box sx={{ mt: 2 }}>
          {activeStep === 0 && (
            <Fade in>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {isReconfiguring 
                    ? `Update your API key for ${provider}. Your existing key will be replaced.`
                    : `Enter your API key to connect to ${provider}. Your key will be stored securely.`
                  }
                </Typography>
                <TextField
                  fullWidth
                  autoFocus
                  label="API Key"
                  type={showApiKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setTouched(true);
                    setLocalStatus('idle');
                    setErrorMsg("");
                    setTestSuccess(false);
                  }}
                  error={touched && !apiKey}
                  helperText={touched && !apiKey ? "Please enter a valid API key" : ""}
                  disabled={localStatus === "testing" || localStatus === "saving" || isLoading}
                  placeholder="Enter your API key..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Key sx={{ color: providerColor, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowApiKey(!showApiKey)}
                          edge="end"
                        >
                          {showApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.background.paper, 0.6),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                      },
                    },
                  }}
                />
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1.5, display: 'block' }}>
                  💡 Get your API key from the {provider} platform
                </Typography>
              </Box>
            </Fade>
          )}

          {activeStep === 1 && (
            <Fade in>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Testing your API key connection...
                </Typography>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <CircularProgress size={28} thickness={4} sx={{ color: providerColor }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Testing connection...
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Verifying your API key with {provider}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Fade>
          )}

          {activeStep === 2 && (
            <Fade in>
              <Box>
                {testSuccess ? (
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.success.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleOutline sx={{ color: theme.palette.success.main, fontSize: 32 }} />
                      <Box>
                        <Typography variant="body1" fontWeight={600} color="success.main">
                          Connection Successful!
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Your API key is valid and ready to use
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ) : (
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.error.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <WarningOutlined sx={{ color: theme.palette.error.main, fontSize: 32 }} />
                      <Box>
                        <Typography variant="body1" fontWeight={600} color="error.main">
                          Connection Failed
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {errorMsg || 'Please check your API key and try again'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Fade>
          )}
        </Box>

        {/* Error Messages */}
        {localStatus === "error" && errorMsg && activeStep !== 2 && (
          <Fade in>
            <Paper
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.error.main, 0.05),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <WarningOutlined sx={{ color: theme.palette.error.main, fontSize: 20, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="error.main">
                  Connection Failed
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  {errorMsg}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Please verify your API key is correct and try again.
                </Typography>
              </Box>
            </Paper>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
        <Button
          onClick={onClose}
          variant="text"
          sx={{ color: theme.palette.text.secondary }}
          disabled={localStatus === "testing" || localStatus === "saving"}
        >
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeStep > 0 && activeStep < 2 && (
            <Button
              onClick={handleBack}
              variant="outlined"
              disabled={localStatus === "testing" || localStatus === "saving"}
              startIcon={<ArrowBack />}
            >
              Back
            </Button>
          )}
          {activeStep < 2 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={!isStepValid() || localStatus === "testing" || localStatus === "saving" || isLoading}
              endIcon={activeStep === 0 ? <ArrowForward /> : null}
              sx={{
                px: 3,
                background: `linear-gradient(135deg, ${providerColor}, ${alpha(providerColor, 0.7)})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${alpha(providerColor, 0.8)}, ${alpha(providerColor, 0.5)})`,
                },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                },
              }}
            >
              {activeStep === 0 ? 'Next' : 'Retry Test'}
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!testSuccess || localStatus === "saving" || localStatus === "success" || isLoading}
              startIcon={localStatus === "saving" ? <CircularProgress size={20} /> : <Save />}
              sx={{
                px: 3,
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.7)})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.8)}, ${alpha(theme.palette.success.main, 0.5)})`,
                },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                },
              }}
            >
              {localStatus === "saving" ? 'Saving...' : 'Save & Activate'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ModelConfigDialog;